import { Select, TextInput } from '@mantine/core'
import { DateInput } from '@/components/ui/DateInput'
import { BOARD_PRIORITY_VALUES } from '@behuman/core-contract'
import { useAssignees, type BoardColumn, type CellValue } from '@/pages/Boards/hooks'
import { CREATE_TASK, FIELD_LABEL_STYLE, RAIL_INPUT_STYLE } from './createTaskTheme'

type Props = {
  column: BoardColumn
  value: CellValue | null
  onChange: (value: CellValue | null) => void
}

/**
 * One controlled input for one board column, rendered into the modal's
 * properties rail. The board's own cell components can't serve here: each reads
 * `item.values[column.id]` and writes straight through `useUpdateItemValue(item)`,
 * and at create time there is no item to bind to.
 */
export const TaskFieldForColumn = ({ column, value, onChange }: Props) => {
  const { data: assigneeData } = useAssignees()
  const asString = typeof value === 'string' ? value : null
  const asRef = typeof value === 'object' && value !== null ? value : null

  switch (column.type) {
    // Both are rendered by the modal itself, not as rail fields: the patient is
    // the header chip plus the rail's locked footer, and a task being filed has
    // no status worth setting (it starts unset on the board). The phone is read from the
    // patient, never entered (PD-1863).
    case 'patient':
    case 'status':
    case 'phone':
      return null

    case 'person': {
      const people = assigneeData?.assignees ?? []
      return (
        <Select
          label={column.label}
          placeholder="Unassigned"
          searchable
          clearable
          data={people.map((person) => ({ value: person.reference, label: person.display }))}
          value={asRef?.reference ?? null}
          onChange={(reference) => {
            const picked = people.find((person) => person.reference === reference)
            onChange(picked ? { reference: picked.reference, display: picked.display } : null)
          }}
          styles={{ label: FIELD_LABEL_STYLE, input: RAIL_INPUT_STYLE }}
        />
      )
    }

    case 'priority':
      // Segmented, not a select: three fixed choices read faster side by side
      // than behind a dropdown, and the rail has the width for them.
      return (
        <div className="flex flex-col gap-1.5">
          <div style={FIELD_LABEL_STYLE}>{column.label}</div>
          <div className="flex gap-1.5" role="group" aria-label={column.label}>
            {/* Ascending, as the design reads: Low sits left, High right. The
                contract lists them High-first, which is the board menu's order. */}
            {[...BOARD_PRIORITY_VALUES].reverse().map((level) => {
              const active = asString === level
              return (
                <button
                  key={level}
                  type="button"
                  aria-pressed={active}
                  // Clicking the active level clears it — the only way back to
                  // "no priority" once one is picked.
                  onClick={() => onChange(active ? null : level)}
                  style={{
                    flex: 1,
                    height: 30,
                    borderRadius: 7,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: active ? 700 : 600,
                    border: `1px solid ${active ? CREATE_TASK.blue : CREATE_TASK.line}`,
                    background: active ? CREATE_TASK.blueSoft : '#FFFFFF',
                    color: active ? CREATE_TASK.blueDeep : '#7C8B98',
                  }}
                >
                  {level}
                </button>
              )
            })}
          </div>
        </div>
      )

    case 'date':
      return (
        <div className="flex flex-col gap-1.5">
          <DateInput
            label={column.label}
            placeholder="No due date"
            clearable
            // The format must carry the year: the board's DateCell uses "MMM D",
            // and a date TYPED into that format is stored under the wrong year.
            valueFormat="MMM D, YYYY"
            // Mantine v8 DateInput hands back a 'YYYY-MM-DD' string (or null) —
            // the same shape DateCell stores.
            value={asString}
            onChange={(picked) => onChange(picked || null)}
            styles={{ label: FIELD_LABEL_STYLE, input: RAIL_INPUT_STYLE }}
          />
          <div className="flex flex-wrap gap-1.5">
            {QUICK_DATES.map((quick) => {
              const iso = quick.iso()
              const active = asString === iso
              return (
                <button
                  key={quick.label}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onChange(active ? null : iso)}
                  style={{
                    height: 26,
                    padding: '0 9px',
                    borderRadius: 999,
                    cursor: 'pointer',
                    fontSize: 12.5,
                    fontWeight: 600,
                    border: `1px solid ${active ? CREATE_TASK.blue : '#DCE3EA'}`,
                    background: active ? CREATE_TASK.blueSoft : '#FFFFFF',
                    color: active ? CREATE_TASK.blueDeep : CREATE_TASK.muted,
                  }}
                >
                  {quick.label}
                </button>
              )
            })}
          </div>
        </div>
      )

    case 'text':
      return (
        <TextInput
          label={column.label}
          value={asString ?? ''}
          onChange={(event) => onChange(event.currentTarget.value || null)}
          styles={{ label: FIELD_LABEL_STYLE, input: RAIL_INPUT_STYLE }}
        />
      )
  }
}

/**
 * Due-date shortcuts. Computed from the local calendar day (not UTC) so "Today"
 * means the coordinator's today, and formatted as the same 'YYYY-MM-DD' string
 * the board stores.
 */
const localIsoDate = (daysFromToday: number): string => {
  const date = new Date()
  date.setDate(date.getDate() + daysFromToday)
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const QUICK_DATES = [
  { label: 'Today', iso: () => localIsoDate(0) },
  { label: 'Tomorrow', iso: () => localIsoDate(1) },
  { label: 'Next week', iso: () => localIsoDate(7) },
]
